import { createTheme } from "@material-ui/core/styles"
import { opacify } from "utils"
import { MuiDialog } from "./Dialog"
import { MuiChip } from "./Chip"
import { MuiSelect } from "./Form"
import { MuiButton } from "./Button"
import { MuiFab } from "./Fab"
import { MuiIconButton } from "./IconButton"
import { MuiToggleButtonGroup, MuiToggleButton } from "./ToggleButton"
import { baseTheme } from "./base"
import { docketTheme } from "./docket"

const primary = docketTheme.colors.primary.normal

const theme = createTheme({
  ...baseTheme,
  overrides: {
    MuiBackdrop: {
      root: {
        backgroundColor: "rgba(0, 0, 0, 0.75)",
      },
    },
    MuiButton,
    MuiToggleButtonGroup,
    MuiToggleButton,
    MuiChip,
    MuiCssBaseline: {
      "@global": {
        html: {
          fontSize: "62.5%",
        },
      },
    },
    MuiContainer: {
      root: {
        [baseTheme.breakpoints.up("xs")]: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
      maxWidthLg: {
        [baseTheme.breakpoints.up("md")]: {
          maxWidth: "110rem",
        },
      },
    },
    MuiDialogActions: {
      root: {
        padding: 0,
      },
    },
    MuiDialog,
    MuiDivider: {
      root: {
        backgroundColor: baseTheme.palette.grey[200],
      },
    },
    MuiExpansionPanel: {
      root: {
        "&$expanded": {
          margin: 0,
        },
        "&$disabled": {
          opacity: 0.5,
          backgroundColor: baseTheme.palette.common.white,
          borderLeft: "0",
          borderRight: "0",
        },
        "&:not(:last-child)": {
          borderBottom: 0,
        },
        border: `0.1rem solid ${docketTheme.colors.grey[80]}`,
      },
    },
    MuiExpansionPanelActions: {
      root: {
        padding: "0 2rem 2rem",
      },
    },
    MuiExpansionPanelDetails: {
      root: {
        flexWrap: "wrap",
        padding: "2rem",
        background: docketTheme.colors.grey[95],
      },
    },
    MuiExpansionPanelSummary: {
      content: {
        "& > :last-child": {
          paddingRight: 0,
        },
        "&$expanded": {
          display: "none",
          minHeight: "0",
        },
        flexGrow: 0,
        margin: 0,
        minHeight: "5.5rem",
        width: "100%",
      },
      root: {
        "&$expanded": {
          "&:hover": {
            backgroundColor: "transparent",
          },
          minHeight: 0,
          borderTop: `0.1rem solid ${docketTheme.colors.grey[80]}`,
          borderBottom: 0,
        },
        "&:hover": {
          backgroundColor: docketTheme.colors.grey[95],
        },
        borderBottom: `0.1rem solid ${docketTheme.colors.grey[80]}`,
        marginBottom: "-0.1rem",
        padding: "0 1rem",
        position: "relative",
        transition: "background-color 150ms cubic-bezier(0.4, 0, 0.2, 1)",
      },
    },
    MuiFab,
    MuiFormControlLabel: {
      root: {
        marginLeft: `-${baseTheme.spacing()}`,
        marginRight: 0,
      },
    },
    MuiInputBase: {
      root: {
        height: "4rem",
      },
      input: {
        textAlign: "inherit",
        "&$disabled": {
          backgroundColor: docketTheme.colors.grey[95],
        },
      },
    },
    MuiFormHelperText: {
      root: {
        marginTop: baseTheme.spacing(0.5),
        color: docketTheme.colors.grey.normal,
        fontSize: "1.2rem",
        lineHeight: "1.6rem",
        "&$disabled": {
          color: docketTheme.colors.grey[40],
        },
      },
      contained: {
        margin: "0.8rem 0 0 0",
      },
    },
    MuiIconButton,
    MuiList: {
      padding: {
        paddingBottom: "0.5rem",
        paddingTop: "0.5rem",
      },
    },
    MuiListItem: {
      root: {
        paddingBottom: "0.3rem",
        paddingTop: "0.3rem",
      },
    },
    MuiMenuItem: {
      gutters: {
        paddingLeft: "2rem",
        paddingRight: "2rem",
      },
      root: {
        marginBottom: "0.1rem",
        marginTop: "0.1rem",
      },
    },
    MuiOutlinedInput: {
      input: {
        backgroundColor: baseTheme.palette.common.white,
        padding: "0 1.2rem",
        height: "4rem",
        fontSize: "1.4rem",
        color: baseTheme.palette.text.primary,
      },
      notchedOutline: {
        top: "-0.4rem",
        borderWidth: "0.1rem",
        "& legend": {
          height: "1rem",
        },
      },
      root: {
        "& $notchedOutline": {
          borderColor: baseTheme.palette.grey[500],
        },
        "&:hover:not($disabled):not($focused):not($error) $notchedOutline": {
          borderColor: baseTheme.palette.grey[500],
        },
      },
    },
    MuiPaper: {
      root: {
        background: "transparent",
      },
      elevation1: {
        WebkitBoxShadow: `0 0.2rem 0.4rem 0 ${docketTheme.colors.boxShadow.dark}`,
        boxShadow: `0 0.2rem 0.4rem 0 ${docketTheme.colors.boxShadow.dark}`,
      },
      elevation2: {
        WebkitBoxShadow: `0 0.2rem 0.4rem 0.3rem ${docketTheme.colors.boxShadow.normal}`,
        boxShadow: `0 0.2rem 0.4rem 0.3rem ${docketTheme.colors.boxShadow.normal}`,
      },
      elevation3: {
        WebkitBoxShadow: `0 0.1rem 0 0 ${docketTheme.colors.grey[90]}, 0 -0.1rem 0 0 ${docketTheme.colors.grey[90]}`,
        boxShadow: `0 0.1rem 0 0 ${docketTheme.colors.grey[90]}, 0 -0.1rem 0 0 ${docketTheme.colors.grey[90]}`,
      },
    },
    MuiCheckbox: {
      root: {
        padding: baseTheme.spacing(),
      },
    },
    MuiRadio: {
      root: {
        padding: baseTheme.spacing(),
      },
    },
    MuiFormGroup: {
      row: {
        "& .MuiFormControlLabel-root:not(:first-of-type)": {
          marginLeft: 0,
        },
      },
    },
    MuiSelect,
    MuiSnackbar: {
      anchorOriginTopCenter: {
        [baseTheme.breakpoints.up("sm")]: {
          transform: "translateX(calc(-50% + 4.5rem))",
        },
      },
    },
    MuiSvgIcon: {
      root: {
        fontSize: `${baseTheme.spacing(3)}`,
      },
      fontSizeLarge: {
        fontSize: "3.6rem",
      },
    },
    MuiTableCell: {
      root: {
        borderBottom: `1px solid ${docketTheme.colors.grey[90]}`,
        padding: "0.4rem 1.2rem",
        "&:first-child": {
          paddingLeft: "1rem",
        },
        "&:last-child": {
          paddingRight: "1rem",
        },
        letterSpacing: 0,
        lineHeight: 1.5,
      },
      sizeSmall: {
        padding: "0.4rem",
        "&:first-child": {
          paddingLeft: "0.8em",
        },
        "&:last-child": {
          paddingRight: "0.8rem",
        },
      },
    },
    MuiTableRow: {
      root: {
        "&$hover": {
          cursor: "pointer",
        },
        "&$hover:hover": {
          backgroundColor: opacify(primary, 0.1),
        },
        "&$selected": {
          backgroundColor: docketTheme.colors.primary.light,
          borderColor: primary,
        },
        height: "5.5rem",
      },
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.95)",
        fontSize: "1.2rem",
      },
    },
    MuiTypography: {
      h3: {
        fontWeight: 500,
      },
      gutterBottom: {
        marginBottom: "0.8rem",
      },
    },
  },
  typography: {
    body1: {
      fontSize: docketTheme.typography.fontSize,
      lineHeight: "2rem",
    },
    body2: {
      fontSize: docketTheme.typography.fontSize,
      letterSpacing: 0,
      lineHeight: "2rem",
    },
    button: {
      fontSize: docketTheme.typography.fontSize,
    },
    caption: {
      color: baseTheme.palette.grey[700],
      letterSpacing: 0,
      lineHeight: 1.33,
    },
    h1: {
      fontSize: "3rem",
      fontWeight: 400,
    },
    h2: {
      fontSize: "2.4rem",
      fontWeight: 500,
    },
    h3: {
      fontSize: "2rem",
      fontWeight: 500,
    },
    h4: {
      fontSize: "1.6rem",
      fontWeight: 700,
    },
    h5: {
      fontSize: "1.6rem",
      fontWeight: 700,
    },
    h6: {
      fontSize: "1.6rem",
      fontWeight: 700,
    },
    fontFamily: "'Roboto', sans-serif",
    htmlFontSize: 10,
  },
})

export const pickerMuiTheme = createTheme({
  ...theme,
  overrides: {
    ...theme.overrides,
    MuiToolbar: {
      root: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "center",
        minHeight: 100,
        backgroundColor: theme.palette.primary.main,
      },
    },
    MuiPickersCalendarHeader: {
      switchHeader: {
        marginLeft: "1.5rem",
        marginRight: "1.5rem",
      },
    },
    MuiFormControl: {
      root: {
        width: "100%",
      },
    },
    MuiPickersToolbar: {
      toolbarTxt: {
        fontSize: "1.8rem",
      },
      text: {
        fontSize: "3.2rem",
      },
    },
    MuiPickersYear: {
      root: {
        "&:hover": {
          background: "rgba(0, 0, 0, 0.04)",
        },
      },
      yearSelected: {
        fontSize: "1.6rem",
      },
    },
    MuiButton: {
      ...theme.overrides?.MuiButton,
      label: {
        color: theme.palette.common.white,
      },
    },
  },
})

export { docketTheme } from "./docket"
export default theme
